import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { parseSyntheticStageId } from '@/features/pipelines/utils/stagePaths';
import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import {
  mapPipelineRunSummaryDto,
  mapPipelineDetailDto,
  mapPipelineStageExecutionListItemDto,
  mapPipelineListItemDto,
  mapPipelineStageListItemDto,
  mapQueryResult,
} from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { mapMetricSorts } from '@/shared/data/metricSortFields';
import { mergeProjectFilters } from '@/shared/data/queryFilters';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import type { GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  BuildStatus,
  PipelineDetailDto,
  PipelineListItemDto,
  PipelineRunSummaryDto,
  PipelineStageExecutionListItemDto,
  PipelineStageListItemDto,
  PipelineProvider,
  PipelineSummaryDto,
  QueryResult,
  QuerySort,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

const normalizeNullableString = (value?: string | null): string | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export type PipelineFilterCountsDto = {
  pipelines: Record<string, number>;
  tags: Record<string, number>;
};

export type PipelineStageFilterCountsDto = {
  pipelines: Record<string, number>;
  stages: Record<string, number>;
  tags: Record<string, number>;
};

export const queryPipelines = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: GridFilters;
    search?: string;
    metricsStartDay?: string;
    metricsEndDay?: string;
    previousMetricsStartDay?: string;
    previousMetricsEndDay?: string;
    include?: PipelineInclude;
  },
): Promise<QueryResult<PipelineListItemDto>> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  const sorts = input?.sorts
    ? mapMetricSorts(input.sorts)
    : [{ field: 'createdAt', direction: 'desc' }];
  try {
    const result = await apiClient.post<QueryResult<PipelineListItemDto>>(
      'pipelines/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        search: input?.search,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
        previousMetricsStartDay: input?.previousMetricsStartDay,
        previousMetricsEndDay: input?.previousMetricsEndDay,
        include: input?.include,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapPipelineListItemDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryPipelineSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: GridFilters;
    metricsStartDay?: string;
    metricsEndDay?: string;
  },
): Promise<PipelineSummaryDto> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  const sorts = input?.sorts ? mapMetricSorts(input.sorts) : [];
  try {
    return await apiClient.post<PipelineSummaryDto>(
      'pipelines/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryPipelineRunSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: GridFilters;
    metricsStartDay?: string;
    metricsEndDay?: string;
    latestOnly?: boolean;
  },
): Promise<PipelineRunSummaryDto> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  const sorts = input?.sorts ? mapMetricSorts(input.sorts) : [];
  try {
    const result = await apiClient.post<PipelineRunSummaryDto>(
      'pipeline-executions/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
        latestOnly: input?.latestOnly ?? false,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapPipelineRunSummaryDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryPipelineFilterCounts = async (
  projectId: string | null,
  organisationId: string | null | undefined,
  input: {
    metricsStartDay?: string;
    metricsEndDay?: string;
    filters: {
      pipelines: GridFilters;
      tags: GridFilters;
    };
  },
): Promise<PipelineFilterCountsDto> => {
  const token = ensureToken();
  const withProjectFilters = (filters: GridFilters): GridFilters =>
    mergeProjectFilters(filters, projectId);
  try {
    return await apiClient.post<PipelineFilterCountsDto>(
      'pipelines/filter-counts',
      {
        metricsStartDay: input.metricsStartDay,
        metricsEndDay: input.metricsEndDay,
        filters: {
          pipelines: withProjectFilters(input.filters.pipelines),
          tags: withProjectFilters(input.filters.tags),
        },
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryPipelineStages = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: GridFilters;
    search?: string;
  },
): Promise<QueryResult<PipelineStageListItemDto>> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  try {
    const result = await apiClient.post<QueryResult<PipelineStageListItemDto>>(
      'stages/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts: input?.sorts ?? [{ field: 'startedAt', direction: 'desc' }],
        filters: combinedFilters,
        search: input?.search,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapPipelineStageListItemDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryPipelineStageFilterCounts = async (
  projectId: string | null,
  organisationId: string | null | undefined,
  input: {
    filters: {
      pipelines: GridFilters;
      stages: GridFilters;
      tags: GridFilters;
    };
  },
): Promise<PipelineStageFilterCountsDto> => {
  const token = ensureToken();
  const withProjectFilters = (filters: GridFilters): GridFilters =>
    mergeProjectFilters(filters, projectId);
  try {
    return await apiClient.post<PipelineStageFilterCountsDto>(
      'stages/filter-counts',
      {
        filters: {
          pipelines: withProjectFilters(input.filters.pipelines),
          stages: withProjectFilters(input.filters.stages),
          tags: withProjectFilters(input.filters.tags),
        },
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryPipelineStageExecutions = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: GridFilters;
    search?: string;
  },
): Promise<QueryResult<PipelineStageExecutionListItemDto>> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  try {
    const result = await apiClient.post<QueryResult<PipelineStageExecutionListItemDto>>(
      'stage-runs/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts: input?.sorts ?? [{ field: 'startedAt', direction: 'desc' }],
        filters: combinedFilters,
        search: input?.search,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapPipelineStageExecutionListItemDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getPipelineStageById = async (
  id: string,
  organisationId?: string | null,
): Promise<PipelineStageListItemDto | null> => {
  const parsedStageId = parseSyntheticStageId(id);
  const filters = parsedStageId
    ? [
        ...buildEqFilter('pipelineId', parsedStageId.pipelineId),
        ...buildEqFilter('stageName', parsedStageId.stageName),
      ]
    : buildEqFilter('id', id);
  const result = await queryPipelineStages(null, organisationId, {
    page: 1,
    pageSize: 1,
    sorts: [{ field: 'stageName', direction: 'asc' }],
    filters,
  });

  return result.items[0] ?? null;
};

export const getPipelineStageExecutionById = async (
  id: string,
  organisationId?: string | null,
): Promise<PipelineStageExecutionListItemDto | null> => {
  const result = await queryPipelineStageExecutions(null, organisationId, {
    page: 1,
    pageSize: 1,
    sorts: [{ field: 'startedAt', direction: 'desc' }],
    filters: buildEqFilter('id', id),
  });

  return result.items[0] ?? null;
};

export const getPipelineById = async (
  id: string,
  organisationId?: string | null,
): Promise<PipelineDetailDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.get<PipelineDetailDto>(`pipelines/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapPipelineDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getPipelineBySlug = async (
  slug: string | null,
  organisationId?: string | null,
): Promise<PipelineDetailDto | null> => {
  if (!slug) {
    return null;
  }
  try {
    return await getPipelineById(slug, organisationId);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) {
      throw err;
    }
    // Fall back to slug lookup when the id fetch fails.
  }

  const pageSize = 100;
  let page = 1;

  for (;;) {
    const result = await queryPipelines(null, organisationId, {
      page,
      pageSize,
      sorts: [{ field: 'createdAt', direction: 'desc' }],
      filters: [],
    });

    const match = result.items.find((item) => resolvePipelineSlug(item) === slug) ?? null;
    if (match) {
      return await getPipelineById(match.id, organisationId);
    }

    if (result.items.length < pageSize || page * pageSize >= result.total) {
      return null;
    }

    page += 1;
  }
};

export const updatePipelineStage = async (
  input: { id: string; stageName: string },
  organisationId?: string | null,
): Promise<PipelineStageListItemDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.patch<PipelineStageListItemDto>(
      `stages/${input.id}`,
      { stageName: normalizeNullableString(input.stageName) ?? input.stageName },
      {
        retries: 1,
        orgId: organisationId ?? undefined,
        token,
      },
    );
    return mapPipelineStageListItemDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const deletePipelineStage = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<{ status: 'deleted' }>(`stages/${input.id}/delete`, undefined, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const updatePipelineStageExecution = async (
  input: {
    id: string;
    status?: BuildStatus;
    startedAt?: Date | null;
    finishedAt?: Date | null;
    durationMs?: number | null;
  },
  organisationId?: string | null,
): Promise<PipelineStageExecutionListItemDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.patch<PipelineStageExecutionListItemDto>(
      `stage-runs/${input.id}`,
      {
        status: input.status,
        startedAt: input.startedAt ?? null,
        finishedAt: input.finishedAt ?? null,
        durationMs: input.durationMs ?? null,
      },
      {
        retries: 1,
        orgId: organisationId ?? undefined,
        token,
      },
    );
    return mapPipelineStageExecutionListItemDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const deletePipelineStageExecution = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`stage-runs/${input.id}/delete`, undefined, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const deletePipelineExecution = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`pipeline-executions/${input.id}/delete`, undefined, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const createPipeline = async (
  input: {
    projectId: string;
    provider: PipelineProvider;
    pipelineKey?: string | null;
    name: string;
    pipelineUrl?: string | null;
    defaultBranch?: string | null;
    tags?: string[];
  },
  organisationId?: string | null,
): Promise<PipelineDetailDto> => {
  const token = ensureToken();
  const body = {
    projectId: input.projectId.trim(),
    provider: input.provider,
    pipelineKey: normalizeNullableString(input.pipelineKey),
    name: input.name.trim(),
    pipelineUrl: normalizeNullableString(input.pipelineUrl),
    defaultBranch: normalizeNullableString(input.defaultBranch),
    tags: input.tags,
  };
  try {
    const result = await apiClient.post<PipelineDetailDto>('pipelines', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapPipelineDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updatePipeline = async (
  input: {
    id: string;
    provider?: PipelineProvider;
    pipelineKey?: string | null;
    name?: string | null;
    pipelineUrl?: string | null;
    defaultBranch?: string | null;
    tags?: string[];
  },
  organisationId?: string | null,
): Promise<PipelineDetailDto> => {
  const token = ensureToken();
  const body = {
    provider: input.provider,
    pipelineKey: normalizeNullableString(input.pipelineKey),
    name: input.name === undefined ? undefined : normalizeNullableString(input.name),
    pipelineUrl: normalizeNullableString(input.pipelineUrl),
    defaultBranch: normalizeNullableString(input.defaultBranch),
    tags: input.tags,
  };
  try {
    const result = await apiClient.patch<PipelineDetailDto>(`pipelines/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapPipelineDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export type PipelineInclude = {
  project?: boolean;
  latestRun?: boolean;
  runSummaries?: boolean;
  dailyHistory?: boolean;
  metrics?: boolean;
  metricDeltas?: boolean;
};

export const deletePipeline = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`pipelines/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const restorePipeline = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`pipelines/${input.id}/restore`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
